import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import { Box, Tooltip, Typography } from '@mui/material';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import DragHandleIcon from '@mui/icons-material/DragHandle';
import * as C from '../../theme/colors.constants';

/**
 * Alça de redimensionamento da grid de Processos.
 *
 * Requisito: "ícone que indique que o usuário controla com o mouse para definir
 * o tamanho". A afordância é tripla, para não depender de descoberta:
 *   1. Barra vertical sempre visível na lateral direita do card.
 *   2. Ícone ↔ (SwapHoriz) centralizado numa pastilha navy — o mesmo ícone que
 *      aparece nos prints de referência.
 *   3. Cursor `col-resize` + tooltip explicando o que o arraste faz.
 *
 * Detalhes de implementação que importam:
 * - Os listeners de `mousemove`/`mouseup` vivem no `document`, não no elemento.
 *   Se ficassem no elemento, um arraste rápido "perderia" o cursor e o resize
 *   travaria no meio.
 * - Durante o arraste, `user-select: none` e `cursor: col-resize` são aplicados
 *   no `body`, senão o navegador seleciona o texto da tabela.
 * - Duplo clique alterna entre mínimo e máximo — atalho para quem só quer ver
 *   todas as colunas de uma vez.
 * - Setas esquerda/direita movem 40px quando a alça está focada (acessibilidade
 *   por teclado, já que arrastar não é operável sem mouse).
 */
export default function ResizeHandle({
  largura, min, max, onChange, colunasVisiveis, colunasTotal,
}) {
  const [arrastando, setArrastando] = useState(false);
  const inicio = useRef({ x: 0, largura: 0 });

  const aoMover = useCallback(
    (e) => {
      const delta = e.clientX - inicio.current.x;
      const nova = Math.min(max, Math.max(min, inicio.current.largura + delta));
      onChange(nova);
    },
    [max, min, onChange],
  );

  const aoSoltar = useCallback(() => setArrastando(false), []);

  useEffect(() => {
    if (!arrastando) return undefined;
    document.addEventListener('mousemove', aoMover);
    document.addEventListener('mouseup', aoSoltar);
    const bodyStyle = document.body.style;
    const anterior = { userSelect: bodyStyle.userSelect, cursor: bodyStyle.cursor };
    bodyStyle.userSelect = 'none';
    bodyStyle.cursor = 'col-resize';
    return () => {
      document.removeEventListener('mousemove', aoMover);
      document.removeEventListener('mouseup', aoSoltar);
      bodyStyle.userSelect = anterior.userSelect;
      bodyStyle.cursor = anterior.cursor;
    };
  }, [arrastando, aoMover, aoSoltar]);

  const iniciar = (e) => {
    inicio.current = { x: e.clientX, largura };
    setArrastando(true);
  };

  const alternar = () => onChange(largura > (min + max) / 2 ? min : max);

  const dica = (
    <Box sx={{ textAlign: 'center' }}>
      <Typography variant="caption" sx={{ display: 'block', fontWeight: 700 }}>
        Arraste para redimensionar
      </Typography>
      <Typography variant="caption" sx={{ display: 'block' }}>
        → exibe mais colunas · ← oculta colunas
      </Typography>
      <Typography variant="caption" sx={{ display: 'block', opacity: 0.8 }}>
        {colunasVisiveis}
        /
        {colunasTotal}
        {' '}
        colunas · duplo clique alterna
      </Typography>
    </Box>
  );

  return (
    <Tooltip title={dica} arrow placement="right" disableInteractive>
      <Box
        // role="slider" e não "separator": a alça define um valor numérico
        // (a largura), e o jsx-a11y 6.2.1 não aceita aria-value* em separator —
        // vira warning, que com CI=true o CRA promove a erro e quebra o build.
        role="slider"
        aria-orientation="vertical"
        aria-label="Redimensionar a grid de processos"
        aria-valuenow={largura}
        aria-valuemin={min}
        aria-valuemax={max}
        tabIndex={0}
        onMouseDown={iniciar}
        onDoubleClick={alternar}
        onKeyDown={(e) => {
          if (e.key === 'ArrowRight') onChange(Math.min(max, largura + 40));
          if (e.key === 'ArrowLeft') onChange(Math.max(min, largura - 40));
        }}
        sx={{
          position: 'absolute',
          top: 0,
          right: -14,
          height: '100%',
          width: 24,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'col-resize',
          zIndex: 3,
          '&:focus-visible': { outline: `2px solid ${C.navy}`, borderRadius: 2 },
          '&:hover .abe-resize-bar, &:focus-visible .abe-resize-bar': {
            backgroundColor: C.navy, opacity: 1,
          },
          '&:hover .abe-resize-pill, &:focus-visible .abe-resize-pill': {
            transform: 'scale(1.12)', boxShadow: '0 4px 12px rgba(13,43,82,0.35)',
          },
        }}
      >
        {/* Trilho vertical */}
        <Box
          className="abe-resize-bar"
          sx={{
            position: 'absolute',
            top: 18,
            bottom: 18,
            width: 3,
            borderRadius: 2,
            backgroundColor: arrastando ? C.navy : '#c3cbd6',
            opacity: arrastando ? 1 : 0.75,
            transition: 'background-color .15s ease, opacity .15s ease',
          }}
        />

        {/* Pastilha com o ícone ↔ */}
        <Box
          className="abe-resize-pill"
          sx={{
            position: 'relative',
            zIndex: 1,
            width: 24,
            height: 46,
            borderRadius: 999,
            backgroundColor: arrastando ? C.navy : C.white,
            border: `1px solid ${arrastando ? C.navy : C.borderGrey}`,
            color: arrastando ? C.white : C.navy,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: C.shadowCard,
            transition: 'transform .15s ease, box-shadow .15s ease, background-color .15s ease',
          }}
        >
          <SwapHorizIcon sx={{ fontSize: 17 }} />
          <DragHandleIcon sx={{ fontSize: 13, opacity: 0.55, mt: -0.5 }} />
        </Box>
      </Box>
    </Tooltip>
  );
}
ResizeHandle.propTypes = {
  largura: PropTypes.number.isRequired,
  min: PropTypes.number.isRequired,
  max: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  colunasVisiveis: PropTypes.number,
  colunasTotal: PropTypes.number,
};
ResizeHandle.defaultProps = {
  colunasVisiveis: null,
  colunasTotal: null,
};
